import { ConfirmSaveModal, CustomizedButton, CustomizedDropdownMenu } from 'components/common';
import Icon from 'components/common/Icons/icon';
import React, { useState } from 'react';
import {
    useMutationGenerateMitigation,
    useMutationGenerateResourcesMitigation,
    useMutationGenerateRiskSummaryMitigation,
    useMutationGenerateSubTaskMitigation,
} from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/queries/commentsMutation';
import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';
import { QueryResponse as CardResponse } from 'api/queries/getMainCard.query';
import { useVersionStore } from 'store/version.store';
import { generatePath, useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';
import { MuiIcon } from 'components/common/muiIcon/muiIcon';

type ModalType = 'MITIGATION' | 'TASK_BREAKDOWN' | 'RESOURCE' | 'RISK_SUMMARY';

interface ICommentsAiMenu {
    isLoadingMitigation: boolean;
    setIsLoadingMitigation: React.Dispatch<React.SetStateAction<boolean>>;
    card: CardResponse;
}
export const CommentsAiMenu = ({ isLoadingMitigation, setIsLoadingMitigation, card }: ICommentsAiMenu) => {
    const [modalType, setModalType] = useState<ModalType | null>(null);
    const { mutate: generateMitigationMutate, isLoading: generateMitigationLoading } = useMutationGenerateMitigation();
    const { mutate: generateSubTaskMitigationMutate, isLoading: generateSubTaskMitigationLoading } =
        useMutationGenerateSubTaskMitigation();
    const { mutate: generateResourceMitigationMutate, isLoading: generateResourceMitigationLoading } =
        useMutationGenerateResourcesMitigation();

    const { mutate: generateRiskSummaryMitigationMutate, isLoading: generateRiskSummaryMitigationLoading } =
        useMutationGenerateRiskSummaryMitigation();

    const { handleSnackBar } = useCustomSnackBar();
    const { sendEventWithDimensions } = useEventWithDimensions();
    const version = useVersionStore((state) => state.version);
    const history = useHistory();
    const { path } = useRouteMatch();
    const { search } = useLocation();
    const params = useParams();

    const isLoading =
        generateMitigationLoading ||
        generateSubTaskMitigationLoading ||
        generateResourceMitigationLoading ||
        generateRiskSummaryMitigationLoading;

    const onConfirm = () => {
        let mutation;
        let label;
        switch (modalType) {
            case 'MITIGATION': {
                mutation = generateMitigationMutate;
                label = 'Mitigation';
                break;
            }
            case 'TASK_BREAKDOWN': {
                mutation = generateSubTaskMitigationMutate;
                label = 'Sub Task Mitigation';
                break;
            }
            case 'RESOURCE': {
                mutation = generateResourceMitigationMutate;
                label = 'Resource Mitigation';
                break;
            }

            case 'RISK_SUMMARY': {
                mutation = generateRiskSummaryMitigationMutate;
                label = 'Resource Mitigation';
                break;
            }
        }

        sendEventWithDimensions({
            category: 'Gen AI',
            action: 'create',
            label: label,
        });
        setModalType(null);
        setIsLoadingMitigation(true);

        const payload = version?.id
            ? {
                  taskId: card.taskId,
                  laneId: card.status.id,
                  isVirtual: card.isVirtual,
                  cardId: card.isVirtual ? null : card.id,
                  versionId: version.id,
              }
            : {
                  taskId: card.taskId,
                  laneId: card.status.id,
                  isVirtual: card.isVirtual,
                  cardId: card.isVirtual ? null : card.id,
              };

        mutation(payload, {
            onSuccess: (response) => {
                const link = `${generatePath(path, { ...params, cardId: response.cardId })}${search}`;
                history.push(link);
            },
            onError: (error) => {
                setIsLoadingMitigation(false);
                DEFAULT_ERROR({ error, handleError: handleSnackBar });
            },
        });
    };

    return (
        <>
            <CustomizedDropdownMenu
                disabled={isLoadingMitigation}
                triggerElement={
                    <CustomizedButton
                        endIcon={<Icon name={'header-arrow-down'} size="15" color={'#2C8FA5'} />}
                        startIcon={<MuiIcon icon={'auto_awesome_icon'} fontSize="8px" color={'#2C8FA5'} />}
                        color={'secondary'}
                        isLoading={isLoadingMitigation}
                    >
                        Generative AI Helper
                    </CustomizedButton>
                }
            >
                <div onClick={() => setModalType('TASK_BREAKDOWN')}>Suggest Task Breakdown</div>
                <div onClick={() => setModalType('RESOURCE')}>Suggest Required Resources</div>
                <div onClick={() => setModalType('MITIGATION')}>Suggest Mitigation</div>
                <div onClick={() => setModalType('RISK_SUMMARY')}>Summarize Risk</div>
            </CustomizedDropdownMenu>
            <ConfirmSaveModal
                isShowModal={modalType !== null}
                onHideModal={() => setModalType(null)}
                onConfirm={onConfirm}
                actionButtonLabel={'Yes'}
                isLoading={isLoading}
            >
                A new comment with the Generative AI response will be added to this card. <br /> Note this request might
                take a while… <br /> You’ll get a notification when its ready. <br /> Continue?
            </ConfirmSaveModal>
        </>
    );
};
